import { Link } from "react-router-dom";
import styled from "styled-components";
import { cPath } from "../../constants";
import { trackOutboundClick } from "../../utils/analytics";
import { getUpcomingExpiry } from "../../utils/offerExpiry";
import { formatDiscountOff } from "../../utils/offerDisplay";
import {
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Eye,
  Share2,
  Bookmark,
  ExternalLink,
} from "lucide-react";

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 1.5rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const ImgCol = styled.div`
  width: 100%;
  @media (min-width: 768px) {
    width: 20rem;
    flex-shrink: 0;
  }
`;

const ImgBox = styled.div`
  aspect-ratio: 1;
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  background: hsl(${({ theme }) => theme.colors.muted});
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Exclusive = styled.span`
  font-size: 0.75rem;
  background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.1);
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
`;

const Meta = styled.span`
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-left: 0.5rem;
`;

const H1 = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const FreeShip = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.accent});
  margin: 0 0 0.75rem;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Sale = styled.span`
  font-size: 2.25rem;
  font-weight: 800;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const Orig = styled.span`
  font-size: 1.25rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  text-decoration: line-through;
`;

const Off = styled.span`
  font-size: 1.125rem;
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.foreground});
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted});
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const IconBtnActive = styled(IconBtn)`
  border-color: hsl(${({ theme }) => theme.colors.primary});
  background: hsl(${({ theme }) => theme.colors.primary} / 0.08);
  color: hsl(${({ theme }) => theme.colors.primary});
`;

const PrimaryA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.625rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: 1rem;
  &:hover {
    opacity: 0.9;
  }
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.625rem 1.5rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  &:hover {
    opacity: 0.9;
  }
`;

const CodeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const CodeBox = styled.div`
  border: 2px dashed hsl(${({ theme }) => theme.colors.accent} / 0.6);
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.5rem 1rem;
  background: hsl(${({ theme }) => theme.colors.accent} / 0.1);
  font-family: ui-monospace, monospace;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.08em;
`;

const CopyBtn = styled.button`
  font-size: 0.875rem;
  font-weight: 500;
  color: hsl(${({ theme }) => theme.colors.primary});
  text-decoration: underline;
`;

const Err = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.8125rem;
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
`;

export default function CouponMainDealCard({
  coupon,
  live = false,
  isAuthenticated = false,
  voteBusy = false,
  likeBusy = false,
  engagementError,
  onVoteUp,
  onVoteDown,
  onBookmarkToggle,
  onCopyCode,
  storeSlug,
  merchantUrl,
}) {
  const myVote = coupon.myVote;
  const UpBtn = live && myVote === 1 ? IconBtnActive : IconBtn;
  const DownBtn = live && myVote === -1 ? IconBtnActive : IconBtn;
  const upcomingExpiry = getUpcomingExpiry(
    coupon.endDate || coupon.expiresAt || coupon.expirationDate
  );

  return (
    <Card>
      {engagementError ? <Err>{engagementError}</Err> : null}
      <Row>
        {coupon.productImage && (
          <ImgCol>
            <ImgBox>
              <Img src={coupon.productImage} alt={coupon.title} />
            </ImgBox>
          </ImgCol>
        )}
        <Body>
          <div>
            {coupon.exclusive && <Exclusive>EXCLUSIVE</Exclusive>}
            <Meta>
              {coupon.postedBy} posted {coupon.postedAt}
              {coupon.verified && coupon.updatedAt ? (
                <> · Verified {new Date(coupon.updatedAt).toLocaleDateString()}</>
              ) : null}
            </Meta>
          </div>
          <H1>{coupon.title}</H1>
          {coupon.freeShipping && <FreeShip>+ Free Shipping</FreeShip>}
          <PriceRow>
            {coupon.salePrice && <Sale>{coupon.salePrice}</Sale>}
            {coupon.originalPrice && <Orig>{coupon.originalPrice}</Orig>}
            {formatDiscountOff(coupon) ? (
              <Off>{formatDiscountOff(coupon)}</Off>
            ) : coupon.type === "deal" && !coupon.salePrice && !coupon.discountedPrice ? (
              <Off>Check current price at retailer</Off>
            ) : null}
          </PriceRow>
          <Actions>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <UpBtn
                type="button"
                disabled={live && voteBusy}
                onClick={onVoteUp}
                title={
                  live && !isAuthenticated
                    ? "Sign in to vote"
                    : "Helpful (upvote)"
                }
              >
                <ThumbsUp size={16} />
              </UpBtn>
              <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                {coupon.upvotes ?? 0}
              </span>
              <DownBtn
                type="button"
                disabled={live && voteBusy}
                onClick={onVoteDown}
                title={
                  live && !isAuthenticated
                    ? "Sign in to vote"
                    : "Not helpful (downvote)"
                }
              >
                <ThumbsDown size={16} />
              </DownBtn>
              {live && (coupon.downvotes ?? 0) > 0 ? (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "hsl(220 10% 46%)",
                  }}
                >
                  {coupon.downvotes} down
                </span>
              ) : null}
            </div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.875rem",
                color: "hsl(220 10% 46%)",
              }}
            >
              <MessageSquare size={16} /> {coupon.comments} Comments
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.875rem",
                color: "hsl(220 10% 46%)",
              }}
            >
              <Eye size={16} /> {Number(coupon.views || 0).toLocaleString()} Views
            </span>
            {upcomingExpiry ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: "0.875rem",
                  color: "hsl(220 10% 46%)",
                }}
              >
                Expires {upcomingExpiry.toLocaleDateString()}
              </span>
            ) : null}
          </Actions>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            {merchantUrl ? (
              <PrimaryA
                href={merchantUrl}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() =>
                  trackOutboundClick({
                    url: merchantUrl,
                    label: `Get deal at ${coupon.storeName}`,
                    storeSlug,
                    couponId: coupon.id,
                  })
                }
              >
                <ExternalLink size={16} />
                Get Deal at {coupon.storeName}
              </PrimaryA>
            ) : storeSlug ? (
              <PrimaryLink
                to={cPath(`store/${storeSlug}`)}
                onClick={() =>
                  trackOutboundClick({
                    url: cPath(`store/${storeSlug}`),
                    label: `Store page ${coupon.storeName}`,
                    storeSlug,
                    couponId: coupon.id,
                  })
                }
              >
                <ExternalLink size={16} />
                View {coupon.storeName} coupons
              </PrimaryLink>
            ) : (
              <PrimaryA href="#" onClick={(e) => e.preventDefault()}>
                <ExternalLink size={16} />
                Get Deal at {coupon.storeName}
              </PrimaryA>
            )}
            <IconBtn type="button">
              <Share2 size={16} />
            </IconBtn>
            <IconBtn
              type="button"
              disabled={live && likeBusy}
              onClick={live ? onBookmarkToggle : undefined}
              title={
                live && !isAuthenticated
                  ? "Sign in to save"
                  : coupon.isLiked
                    ? "Remove from saved"
                    : "Save coupon"
              }
              style={
                live && coupon.isLiked
                  ? {
                      borderColor: "hsl(348 60% 35%)",
                      color: "hsl(348 60% 35%)",
                    }
                  : undefined
              }
            >
              <Bookmark
                size={16}
                fill={live && coupon.isLiked ? "hsl(348 60% 35%)" : "none"}
              />
            </IconBtn>
          </div>
          {coupon.code && (
            <CodeRow>
              <span
                style={{
                  fontSize: "0.875rem",
                  color: "hsl(220 10% 46%)",
                }}
              >
                Code:
              </span>
              <CodeBox>{coupon.code}</CodeBox>
              <CopyBtn type="button" onClick={onCopyCode}>
                Copy
              </CopyBtn>
            </CodeRow>
          )}
        </Body>
      </Row>
    </Card>
  );
}
